import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { safeFormatDate } from '../shared/utils';
import apiCall from '../shared/apiCall';
import { HELP_TEXT } from '../shared/helpText';
import { generateShareUrl } from '../share/utils/shareUtils';

// Function to clean plan data for sharing (remove private information)
const cleanPlanForSharing = (planData, exclusions = {}, options = {}) => {
  // Deep clone to avoid mutating the original plan data
  const cleanedData = JSON.parse(JSON.stringify(planData));
  
  const {
    includeTodos = false,
    includeCosts = false,
    includeNotes = false,
    includePackingList = false,
    includeInfo = false
  } = options;

  // Remove or clear private data based on options (same logic as todos, notes)
  if (!includeCosts) {
    cleanedData.additionalCosts = [];
  }
  if (!includeTodos) {
    // Keep default todos (source: 'plan') even when todos are excluded
    cleanedData.todos = (cleanedData.todos || []).filter(todo => todo.source === 'plan');
  }
  if (!includePackingList) {
    cleanedData.packingItems = [];
  }
  if (!includeInfo) {
    cleanedData.infoSections = [];
  }
  
  // Get excluded activity IDs (empty array = all included, non-empty = those IDs are excluded)
  // Convert to strings for consistent comparison
  const excludedIds = (exclusions.excludedActivityIds || []).map(id => String(id));
  
      // Clean day-specific todos based on options
      if (cleanedData.days) {
        cleanedData.days = cleanedData.days.map(day => {
          let activities = day.activities || [];
          
          // Filter activities: if excludedIds is empty, include all; otherwise exclude those in the array
          if (excludedIds.length > 0) {
            activities = activities.filter(activity => !excludedIds.includes(String(activity.id)));
          }
          
          // For day todos: if includeTodos is false, keep only default todos (source: 'plan')
          let dayTodos = [];
          if (includeTodos) {
            dayTodos = day.todos || [];
          } else {
            // Keep default todos even when todos are excluded
            dayTodos = (day.todos || []).filter(todo => todo.source === 'plan');
          }
          
          return {
            ...day,
            todos: dayTodos,
            activities: activities
          };
        });
      }
  
  // Clear cost values (set to empty) when not including costs — empty = don't show; 0 = show zero
  const cleanCosts = (obj) => {
    if (obj && typeof obj === 'object') {
      if (obj.cost && !includeCosts) {
        obj.cost = { ...obj.cost, value: '' };
      }
      // Recursively clean nested objects
      Object.keys(obj).forEach(key => {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          if (Array.isArray(obj[key])) {
            obj[key].forEach(item => cleanCosts(item));
          } else {
            cleanCosts(obj[key]);
          }
        }
      });
    }
  };
  
  // Clean costs from all activities and filter private links
  if (cleanedData.days) {
    cleanedData.days.forEach(day => {
      if (day.activities) {
        day.activities.forEach(activity => {
          if (!includeCosts) {
            cleanCosts(activity);
          }
          
          // Filter out private links (keep only public links)
          if (activity.links && Array.isArray(activity.links)) {
            activity.links = activity.links.filter(link => link.isPrivate === false);
          }
          
          // Clear notes if not including notes
          if (!includeNotes) {
            activity.notes = '';
          }
        });
      }
    });
  }
  
  // Clean any additional cost fields if not including costs
  if (!includeCosts) {
    cleanCosts(cleanedData);
  }
  
  return cleanedData;
};

export default function ShareOptionsModal({
  show,
  onHide,
  parsedData,
  planId,
  plan,
  onPlanUpdate,
  onPlanRefetch,
  exclusions,
  setExclusions,
  expandedSections,
  setExpandedSections,
  accountSettings = {}
}) {
  const { t } = useTranslation();
  const [isSharing, setIsSharing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [error, setError] = useState('');
  const [showShareLink, setShowShareLink] = useState(false);
  const [allowContact, setAllowContact] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null); // { url, name } or null
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [shareOptions, setShareOptions] = useState({
    includeTodos: true, // Default: include todos (especially default todos)
    includeNotes: false, // Default: exclude notes (user can enable)
    includePacking: false, // Default: exclude packing (user can enable)
    isTemplate: false // When true, shared page shows only day numbers and times (no dates)
  });
  const lastExclusionsRef = useRef(JSON.stringify(exclusions.excludedActivityIds || []));

  // Check if share link already exists (include ?lang= so shared page opens in plan language)
  const existingShareId = plan?.sharingData?.externalShare?.shareId;
  const existingShareUrl = existingShareId ? generateShareUrl(existingShareId, { language: plan?.language }) : '';
  
  // Sync showShareLink and shareUrl when modal opens and existing share link is detected
  // Also load saved exclusions from plan data
  useEffect(() => {
    if (show) {
      if (existingShareId) {
        // Existing share link found - show it
        setShowShareLink(true);
        setShareUrl(existingShareUrl);
        
        // Load saved exclusions from plan data
        const savedExcludeIds = plan?.sharingData?.externalShare?.excludeIds || [];
        // Convert to strings for consistent comparison
        const savedExcludeIdsStr = savedExcludeIds.map(id => String(id));
        setExclusions({ excludedActivityIds: savedExcludeIdsStr });
        lastExclusionsRef.current = JSON.stringify(savedExcludeIdsStr);
      } else if (!shareUrl) {
        // No existing link and no newly created link - hide it
        setShowShareLink(false);
        // Reset exclusions to empty (all included)
        setExclusions({ excludedActivityIds: [] });
        lastExclusionsRef.current = JSON.stringify([]);
      }
      
      // Always load saved allowContact setting from plan data (regardless of share link existence)
      const savedAllowContact = plan?.sharingData?.externalShare?.allowContact ?? false;
      setAllowContact(savedAllowContact);
      
      // Load saved share options from plan data
      // Note: cleanSectionsData uses true = excluded, false = included
      const savedCleanSections = plan?.sharingData?.externalShare?.cleanSectionsData || {};
      setShareOptions({
        includeTodos: savedCleanSections.todo === undefined ? true : !savedCleanSections.todo, // Default to true (include todos)
        includeNotes: savedCleanSections.notes === undefined ? false : !savedCleanSections.notes, // Default to false (exclude notes)
        includePacking: savedCleanSections.packing === undefined ? false : !savedCleanSections.packing, // Default to false (exclude packing)
        includeAdditionalInfo: savedCleanSections.info === undefined ? false : !savedCleanSections.info, // Default to false (exclude Additional Info)
        isTemplate: plan?.sharingData?.externalShare?.isTemplate ?? false
      });
      // Load saved banner from plan data (so dropdown doesn't always show "None")
      const savedBanner = plan?.sharingData?.externalShare?.banner;
      setSelectedBanner(savedBanner && savedBanner.url ? { url: savedBanner.url, name: savedBanner.name || '' } : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, existingShareId, existingShareUrl, plan]);
  
  // Function to update existing share link when user clicks Save
  const handleUpdateShareLink = async () => {
    if (!parsedData || !planId || !existingShareId) return;
    
    setIsUpdating(true);
    setError('');
    
    try {
      // Clean the plan data for sharing with exclusions
      const cleanedPlanData = cleanPlanForSharing(parsedData, exclusions, {
        includeTodos: shareOptions.includeTodos,
        includeCosts: false,
        includeNotes: shareOptions.includeNotes,
        includePackingList: shareOptions.includePacking,
        includeInfo: shareOptions.includeAdditionalInfo
      });

      // Prepare sharingData for external share
      // Convert all activity IDs to strings for consistent storage
      const excludeIds = (exclusions.excludedActivityIds || []).map(id => String(id));
      const sharingData = {
        externalShare: {
          excludeIds,
          allowContact,
          isTemplate: shareOptions.isTemplate,
          banner: selectedBanner && selectedBanner.url ? selectedBanner : undefined,
          cleanSectionsData: {
            cost: true,
            todo: !shareOptions.includeTodos, // true means excluded, false means included
            notes: !shareOptions.includeNotes,
            packing: !shareOptions.includePacking,
            info: !shareOptions.includeAdditionalInfo // true means excluded, false means included
          }
        }
      };

      // Call the shared API to update existing shareable version
      const response = await apiCall('/api/shared', {
        action: 'CREATE_SHARED_PLAN',
        originalPlanId: planId,
        cleanedData: cleanedPlanData,
        planName: parsedData.name || t('planDetail.shareOptions.sharedTripPlan'),
        sharingData: sharingData
      }, 'POST');
      
      if (response.success) {
        // Update lastExclusionsRef to reflect saved state
        lastExclusionsRef.current = JSON.stringify(excludeIds);
        
        // Update plan state via callback
        if (onPlanUpdate && plan) {
          const updatedPlan = { ...plan };
          if (!updatedPlan.sharingData) {
            updatedPlan.sharingData = {};
          }
          updatedPlan.sharingData.externalShare = {
            ...sharingData.externalShare,
            shareId: existingShareId
          };
          onPlanUpdate(updatedPlan);
        }
      } else {
        setError(response.message || t('planDetail.shareOptions.failedToUpdate'));
      }
    } catch (error) {
      console.error('Error updating shareable plan:', error);
      setError(t('planDetail.shareOptions.failedToUpdate'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Collect all transportation and accommodation activities
  const transportationActivities = [];
  const accommodationActivities = [];
  
  if (parsedData?.days) {
    parsedData.days.forEach((day, dayIndex) => {
      if (day.activities) {
        day.activities.forEach(activity => {
          if (activity.type === 'transportation' && activity.id) {
            transportationActivities.push({
              ...activity,
              dayIndex: dayIndex + 1,
              dayDate: day.date
            });
          } else if (activity.type === 'accommodation' && activity.id) {
            accommodationActivities.push({
              ...activity,
              dayIndex: dayIndex + 1,
              dayDate: day.date
            });
          }
        });
      }
    });
  }

  const handleActivityToggle = (activityId, checked) => {
    // Convert to string for consistent comparison
    const activityIdStr = String(activityId);
    const currentExcluded = (exclusions.excludedActivityIds || []).map(id => String(id));
    if (checked) {
      // Checked = include: remove from excluded list
      setExclusions(prev => ({
        ...prev,
        excludedActivityIds: prev.excludedActivityIds.filter(id => String(id) !== activityIdStr)
      }));
    } else {
      // Unchecked = exclude: add to excluded list
      if (!currentExcluded.includes(activityIdStr)) {
        setExclusions(prev => ({
          ...prev,
          excludedActivityIds: [...prev.excludedActivityIds, activityIdStr]
        }));
      }
    }
  };

  const handleCreateShareLink = async () => {
    if (!parsedData || !planId) return;
    
    setIsSharing(true);
    setError('');
    
    try {
      // Clean the plan data for sharing with exclusions
      const cleanedPlanData = cleanPlanForSharing(parsedData, exclusions, {
        includeTodos: shareOptions.includeTodos,
        includeCosts: false,
        includeNotes: shareOptions.includeNotes,
        includePackingList: shareOptions.includePacking,
        includeInfo: shareOptions.includeAdditionalInfo
      });

      // Prepare sharingData for external share
      const sharingData = {
        externalShare: {
          excludeIds: exclusions.excludedActivityIds || [],
          allowContact,
          isTemplate: shareOptions.isTemplate,
          banner: selectedBanner && selectedBanner.url ? selectedBanner : undefined,
          cleanSectionsData: {
            cost: true,
            todo: !shareOptions.includeTodos, // true means excluded, false means included
            notes: !shareOptions.includeNotes,
            packing: !shareOptions.includePacking,
            info: !shareOptions.includeAdditionalInfo // true means excluded, false means included
          }
        }
      };

      // Call the shared API to create a shareable version
      const response = await apiCall('/api/shared', {
        action: 'CREATE_SHARED_PLAN',
        originalPlanId: planId,
        cleanedData: cleanedPlanData,
        planName: parsedData.name || t('planDetail.shareOptions.sharedTripPlan'),
        sharingData: sharingData
      }, 'POST');
      
      if (response.success) {
        const shareId = response.shareId;
        const newShareUrl = generateShareUrl(shareId, { language: plan?.language || parsedData?.language });
        
        setShareUrl(newShareUrl);
        setShowShareLink(true);
        lastExclusionsRef.current = JSON.stringify(exclusions.excludedActivityIds || []);
        
        // Update plan state via callback
        if (onPlanUpdate && plan) {
          const updatedPlan = { ...plan };
          if (!updatedPlan.sharingData) {
            updatedPlan.sharingData = {};
          }
          updatedPlan.sharingData.externalShare = {
            ...sharingData.externalShare,
            shareId: shareId
          };
          onPlanUpdate(updatedPlan);
        }
      } else {
        setError(response.message || 'Failed to create shareable link. Please try again.');
      }
    } catch (error) {
      console.error('Error creating shareable plan:', error);
      setError('Failed to create shareable link. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleDeleteShareLink = async () => {
    if (!planId) return;
    
    setIsSharing(true);
    setError('');
    
    try {
      const response = await apiCall('/api/shared', {
        action: 'DELETE_SHARED_PLAN',
        originalPlanId: planId
      }, 'POST');
      
      if (response.success) {
        // Clear share URL
        setShareUrl('');
        setShowShareLink(false);
        setShowDeleteConfirm(false);
        
        // Update plan state via callback - ensure sharingData is completely cleared
        if (onPlanUpdate && plan) {
          const updatedPlan = { ...plan };
          if (updatedPlan.sharingData) {
            if (updatedPlan.sharingData.externalShare) {
              delete updatedPlan.sharingData.externalShare;
            }
            // If sharingData is now empty or only has groupShare, clean it up
            if (!updatedPlan.sharingData.externalShare && 
                (!updatedPlan.sharingData.groupShare || Object.keys(updatedPlan.sharingData.groupShare).length === 0)) {
              updatedPlan.sharingData = undefined;
            }
          }
          onPlanUpdate(updatedPlan);
        }
      } else {
        setError(response.message || t('planDetail.shareOptions.failedToDelete'));
      }
    } catch (error) {
      console.error('Error deleting share link:', error);
      setError(t('planDetail.shareOptions.failedToDelete'));
    } finally {
      setIsSharing(false);
    }
  };

  const handleClose = () => {
    // Reset state (but keep showShareLink if existingShareId exists)
    setError('');
    // Don't reset exclusions here - they will be reloaded from plan data when modal reopens
    setExpandedSections({ transportation: false, accommodation: false });
    onHide();
  };

  // Determine which share URL to show (prefer newly created, fallback to existing)
  const displayShareUrl = (showShareLink && shareUrl) ? shareUrl : existingShareUrl;
  // Check if share link exists - check existingShareId first (from plan data), then newly created
  const hasShareLink = !!existingShareId || !!shareUrl;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-share me-2"></i>
          {t('planDetail.shareOptions.title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body id="external-share-modal" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Always show activity selection */}
        <div>
          {!hasShareLink && (
            <p className="mb-4">
              {t('planDetail.shareOptions.description')}
            </p>
          )}
          
          {hasShareLink && (
            <p className="mb-4 text-muted">
              <small>{t('planDetail.shareOptions.hasShareLinkDescription')}</small>
            </p>
          )}
          
          {/* Activity Selection Section */}
          {(transportationActivities.length > 0 || accommodationActivities.length > 0) && (
            <div className="mb-4">
              <h6 className="mb-3">{t('planDetail.shareOptions.includeActivities')}</h6>
                
                {/* Transportation Section */}
                {transportationActivities.length > 0 && (
                <div className="mb-3">
                  <div 
                    className="d-flex align-items-center justify-content-between p-2 bg-light rounded cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedSections(prev => ({
                      ...prev,
                      transportation: !prev.transportation
                    }))}
                  >
                    <div className="d-flex align-items-center">
                      <span className="route-icon me-2"></span>
                      <strong>{t('planDetail.shareOptions.transportation')} ({transportationActivities.length})</strong>
                    </div>
                    <i className={`bi bi-chevron-${expandedSections.transportation ? 'up' : 'down'}`}></i>
                  </div>
                  
                  {expandedSections.transportation && (
                    <div className="ms-3 mt-2 mb-2 border-start ps-3" style={{ borderColor: '#dee2e6', borderWidth: '2px' }}>
                      {transportationActivities.map((activity) => (
                        <div key={activity.id} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`include-transportation-${activity.id}`}
                            checked={!exclusions.excludedActivityIds || exclusions.excludedActivityIds.length === 0 || !exclusions.excludedActivityIds.map(id => String(id)).includes(String(activity.id))}
                            onChange={(e) => handleActivityToggle(activity.id, e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor={`include-transportation-${activity.id}`}>
                            <div>
                              <strong>{activity.name}</strong>
                              {activity.dayDate && (
                                <small className="text-muted ms-2">
                                  ({t('planDetail.shareOptions.day')} {activity.dayIndex} - {safeFormatDate(activity.dayDate, 'MMM d')})
                                </small>
                              )}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Accommodation Section */}
              {accommodationActivities.length > 0 && (
                <div className="mb-3">
                  <div 
                    className="d-flex align-items-center justify-content-between p-2 bg-light rounded cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedSections(prev => ({
                      ...prev,
                      accommodation: !prev.accommodation
                    }))}
                  >
                    <div className="d-flex align-items-center">
                      <i className="bi bi-building me-2"></i>
                      <strong>{t('planDetail.shareOptions.accommodation')} ({accommodationActivities.length})</strong>
                    </div>
                    <i className={`bi bi-chevron-${expandedSections.accommodation ? 'up' : 'down'}`}></i>
                  </div>
                  
                  {expandedSections.accommodation && (
                    <div className="ms-3 mt-2 mb-2 border-start ps-3" style={{ borderColor: '#dee2e6', borderWidth: '2px' }}>
                      {accommodationActivities.map((activity) => (
                        <div key={activity.id} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`include-accommodation-${activity.id}`}
                            checked={!exclusions.excludedActivityIds || exclusions.excludedActivityIds.length === 0 || !exclusions.excludedActivityIds.map(id => String(id)).includes(String(activity.id))}
                            onChange={(e) => handleActivityToggle(activity.id, e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor={`include-accommodation-${activity.id}`}>
                            <div>
                              <strong>{activity.name}</strong>
                              {activity.dayDate && (
                                <small className="text-muted ms-2">
                                  ({t('planDetail.shareOptions.day')} {activity.dayIndex} - {safeFormatDate(activity.dayDate, 'MMM d')})
                                </small>
                              )}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Share Options Section */}
        <div className="mb-4">
          <h6 className="mb-3">{t('planDetail.shareToGroups.whatToInclude', { defaultValue: 'What to Include:' })}</h6>
          <div className="ms-2">
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="include-todos"
                checked={shareOptions.includeTodos}
                onChange={(e) => setShareOptions(prev => ({ ...prev, includeTodos: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="include-todos">
                <strong>{t('planDetail.shareToGroups.todos', { defaultValue: 'Todos' })}</strong>
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="include-notes"
                checked={shareOptions.includeNotes}
                onChange={(e) => setShareOptions(prev => ({ ...prev, includeNotes: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="include-notes">
                <strong>{t('planDetail.shareToGroups.notes', { defaultValue: 'Notes' })}</strong>
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="include-packing"
                checked={shareOptions.includePacking}
                onChange={(e) => setShareOptions(prev => ({ ...prev, includePacking: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="include-packing">
                <strong>{t('planDetail.shareToGroups.packingList', { defaultValue: 'Packing list' })}</strong>
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="include-additional-info"
                checked={shareOptions.includeAdditionalInfo}
                onChange={(e) => setShareOptions(prev => ({ ...prev, includeAdditionalInfo: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="include-additional-info">
                <strong>{t('planDetail.shareOptions.includeAdditionalInfo', { defaultValue: 'Additional Info' })}</strong>
              </label>
            </div>
          </div>
        </div>

        {/* Allow Contact Checkbox */}
        <div className="mb-4">
          <Form.Check
            type="switch"
            id="allow-contact-switch"
            label={
              <div className="d-flex align-items-center">
                <span>{t('planDetail.shareOptions.usersCanContactMe')}</span>
                <i 
                  className="bi bi-question-circle-fill text-primary ms-2" 
                  style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                  data-tooltip-id="allow-contact-tooltip"
                  data-tooltip-html={t('planDetail.shareOptions.allowContactTooltip')}
                ></i>
                <Tooltip id="allow-contact-tooltip" place="right" />
              </div>
            }
            checked={allowContact}
            onChange={(e) => setAllowContact(e.target.checked)}
          />
        </div>

        {/* Share as template: hide dates on shared page, show only day numbers and times */}
        <div className="mb-4">
          <Form.Check
            type="switch"
            id="share-as-template-switch"
            label={t('planDetail.shareOptions.shareAsTemplate')}
            checked={shareOptions.isTemplate}
            onChange={(e) => setShareOptions(prev => ({ ...prev, isTemplate: e.target.checked }))}
          />
        </div>
        
        {/* Banner selection (owner brand image on shared page) */}
        {(() => {
          const bannerImages = (accountSettings.images || []).filter(img => img.type === 'banner');
          if (bannerImages.length === 0) return null;
          const bannerValue = selectedBanner?.url ? JSON.stringify(selectedBanner) : '';
          return (
            <div className="mb-4" style={{ maxWidth: '16rem' }}>
              <Form.Group>
                <Form.Label className="fw-semibold">{t('planDetail.shareOptions.banner', { defaultValue: 'Banner on shared page' })}</Form.Label>
                <Form.Select
                  value={bannerValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedBanner(v ? JSON.parse(v) : null);
                  }}
                >
                  <option value="">{t('planDetail.shareOptions.bannerNone', { defaultValue: 'None' })}</option>
                  {bannerImages.map((img, idx) => (
                    <option key={idx} value={JSON.stringify({ url: img.url, name: img.name || '' })}>
                      {img.name || img.url || `Banner ${idx + 1}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          );
        })()}
        
        {/* Privacy Note */}
        <div className="alert alert-info mb-4">
          <i className="bi bi-info-circle me-2"></i>
          {t('planDetail.shareOptions.privacyNote', { defaultValue: 'The following are excluded by default for privacy: costs, private links, and packing list.' })}
        </div>

        {/* Show share link at the bottom if it exists */}
        {hasShareLink && (
          <div className="border-top pt-3 mt-3">
            <div className="d-flex align-items-center bg-light p-3 rounded mb-3">
              <input 
                type="text" 
                className="form-control me-2" 
                value={displayShareUrl}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
              />
              <Button 
                variant="outline-primary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(displayShareUrl);
                    // Show success feedback
                    const button = document.activeElement;
                    const originalIcon = button.innerHTML;
                    button.innerHTML = '<i class="bi bi-check"></i>';
                    button.classList.add('btn-success');
                    button.classList.remove('btn-outline-primary');
                    setTimeout(() => {
                      button.innerHTML = originalIcon;
                      button.classList.remove('btn-success');
                      button.classList.add('btn-outline-primary');
                    }, 1500);
                  } catch (error) {
                    console.log('Clipboard copy failed');
                  }
                }}
                title={t('planDetail.shareOptions.copyToClipboard')}
              >
                <i className="bi bi-clipboard"></i>
              </Button>
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={handleUpdateShareLink}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('planDetail.shareOptions.updating')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    {t('planDetail.shareOptions.update')}
                  </>
                )}
              </Button>
              <Button 
                variant="outline-primary"
                onClick={() => window.open(displayShareUrl, '_blank')}
              >
                <i className="bi bi-box-arrow-up-right me-1"></i>
                {t('planDetail.shareOptions.previewSharedPlan')}
              </Button>
              <Button 
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSharing}
              >
                {isSharing ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t('planDetail.shareOptions.deleting')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash me-1"></i>
                    {t('planDetail.shareOptions.deleteShareLink')}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {hasShareLink ? (
          <Button variant="secondary" onClick={handleClose}>
            {t('planDetail.shareOptions.close')}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              {t('planDetail.shareOptions.cancel')}
            </Button>
            <Button variant="primary" onClick={handleCreateShareLink} disabled={isSharing}>
              {isSharing ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {t('planDetail.shareOptions.creating')}
                </>
              ) : (
                <>
                  <i className="bi bi-share me-2"></i>
                  {t('planDetail.shareOptions.createShareLink')}
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('planDetail.shareOptions.deleteShareLink')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('planDetail.shareOptions.deleteConfirm')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={handleDeleteShareLink} disabled={isSharing}>
            {isSharing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('planDetail.shareOptions.deleting')}
              </>
            ) : (
              <>
                <i className="bi bi-trash me-1"></i>
                {t('planDetail.shareOptions.deleteShareLink')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
}
